import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { useChurch } from '../context/ChurchContext';

const PRESET_COLORS = [
  '#1e40af','#4338ca','#7c3aed','#db2777','#dc2626',
  '#d97706','#16a34a','#0891b2','#0f172a','#334155',
];

const PLAN_LABELS = { free: 'Gratuit', pro: 'Pro', enterprise: 'Entreprise' };
const PLAN_COLORS = { free: '#64748b', pro: '#3b82f6', enterprise: '#7c3aed' };

const s = {
  label: { display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '5px' },
  input: { width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1.5px solid var(--border)', background: 'var(--card-bg)', color: 'var(--text-primary)', fontSize: '0.875rem', boxSizing: 'border-box' },
  section: { background: 'var(--card-bg)', borderRadius: '14px', border: '1.5px solid var(--border)', overflow: 'hidden', marginBottom: 20 },
  sHead: { padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 },
  sTitle: { fontWeight: 700, fontSize: '0.95rem', color: 'var(--text-primary)' },
  sDesc: { fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 1 },
  sBody: { padding: '20px' },
  row2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  btn: (color = '#3b82f6') => ({
    padding: '10px 22px', borderRadius: '8px', border: 'none', cursor: 'pointer',
    background: color, color: '#fff', fontWeight: 600, fontSize: '0.875rem',
    display: 'flex', alignItems: 'center', gap: 7,
  }),
};

const EMPTY_FORM = {
  name: '', pastor: '', city: '', country: "Côte d'Ivoire", address: '',
  phone: '', email: '', website: '', facebook: '', description: '',
  primary_color: '#1e40af',
};

export default function ChurchProfile() {
  const { user } = useAuth();
  const { activeChurchId, canSwitchChurch } = useChurch();

  const [church, setChurch] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  // Pour superadmin/superviseur : éditer l'église sélectionnée dans la navbar
  // Pour admin : éditer son propre church
  const churchId = canSwitchChurch ? activeChurchId : (user?.church_id || null);

  const fetchChurch = useCallback(async () => {
    if (!churchId) return;
    setError('');
    try {
      const res = await api.get(`/churches/${churchId}/`);
      setChurch(res.data);
      setForm({
        name: res.data.name || '',
        pastor: res.data.pastor || '',
        city: res.data.city || '',
        country: res.data.country || "Côte d'Ivoire",
        address: res.data.address || '',
        phone: res.data.phone || '',
        email: res.data.email || '',
        website: res.data.website || '',
        facebook: res.data.facebook || '',
        description: res.data.description || '',
        primary_color: res.data.primary_color || '#1e40af',
      });
      setLogoPreview(res.data.logo_url || null);
      setLogoFile(null);
    } catch {
      setError('Impossible de charger les informations de l\'église.');
    }
  }, [churchId]);

  useEffect(() => {
    setChurch(null);
    setForm(EMPTY_FORM);
    setLogoPreview(null);
    fetchChurch();
  }, [fetchChurch]);

  const handleSave = async () => {
    if (!churchId) return;
    setSaving(true);
    setSaved(false);
    try {
      await api.patch(`/churches/${churchId}/`, form);
      if (logoFile) {
        const fd = new FormData();
        fd.append('logo', logoFile);
        await api.post(`/churches/${churchId}/upload_logo/`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setLogoFile(null);
      }
      await fetchChurch();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError('Erreur lors de la sauvegarde.');
    } finally {
      setSaving(false);
    }
  };

  const removeLogo = async () => {
    if (!churchId) return;
    try {
      const fd = new FormData();
      fd.append('remove_logo', 'true');
      await api.post(`/churches/${churchId}/upload_logo/`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setLogoPreview(null);
      setLogoFile(null);
    } catch { /* ignore */ }
  };

  // Super admin/superviseur sans église sélectionnée
  if (canSwitchChurch && !activeChurchId) {
    return (
      <div style={{ padding: '60px 24px', textAlign: 'center' }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
        </div>
        <div style={{ fontWeight: 700, fontSize: '1.1rem', color: 'var(--text-primary)', marginBottom: 8 }}>Aucune église sélectionnée</div>
        <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Sélectionnez une église dans la barre de navigation pour voir ses informations.</div>
      </div>
    );
  }

  if (!churchId) {
    return (
      <div style={{ padding: '60px 24px', textAlign: 'center' }}>
        <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Vous n'avez pas d'église assignée. Contactez l'administrateur de la plateforme.</div>
      </div>
    );
  }

  if (!church) {
    return <div style={{ padding: '60px', textAlign: 'center', color: 'var(--text-muted)' }}>Chargement...</div>;
  }

  const color = form.primary_color || '#1e40af';

  return (
    <div>
      {/* En-tête avec aperçu */}
      <div style={{ background: `linear-gradient(135deg, ${color} 0%, ${color}bb 100%)`, borderRadius: 16, padding: '24px 28px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 20, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: -40, right: -40, width: 160, height: 160, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
        <div style={{ position: 'relative', flexShrink: 0 }}>
          {logoPreview
            ? <img src={logoPreview} alt="" style={{ width: 80, height: 80, borderRadius: 16, objectFit: 'cover', border: '3px solid rgba(255,255,255,0.4)' }} />
            : <div style={{ width: 80, height: 80, borderRadius: 16, background: 'rgba(255,255,255,0.2)', border: '3px solid rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: '2rem' }}>
                {(form.name || church.name || '?')[0]?.toUpperCase()}
              </div>
          }
          <label style={{ position: 'absolute', bottom: -4, right: -4, background: '#fff', borderRadius: '50%', width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { const f = e.target.files[0]; if (!f) return; setLogoFile(f); setLogoPreview(URL.createObjectURL(f)); }} />
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
          </label>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>Mon église</div>
          <div style={{ color: '#fff', fontWeight: 800, fontSize: '1.3rem', lineHeight: 1.2 }}>{form.name || church.name}</div>
          {form.pastor && <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.82rem', marginTop: 4 }}>Pasteur : {form.pastor}</div>}
          {form.city && <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.78rem', marginTop: 2 }}>📍 {form.city}{form.country ? `, ${form.country}` : ''}</div>}
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <span style={{ background: `${PLAN_COLORS[church.plan] || '#64748b'}30`, color: '#fff', fontSize: '0.72rem', fontWeight: 700, padding: '4px 12px', borderRadius: 20, border: '1px solid rgba(255,255,255,0.3)' }}>
            {PLAN_LABELS[church.plan] || church.plan}
          </span>
          <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.72rem', marginTop: 8 }}>Max {church.max_participants} participants</div>
          <div style={{ color: '#fff', fontWeight: 800, fontSize: '1.4rem' }}>{church.participant_count ?? 0}</div>
          <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem' }}>fidèles</div>
        </div>
      </div>

      {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: '0.875rem' }}>{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 20 }}>
        {/* Identité */}
        <div style={s.section}>
          <div style={s.sHead}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1e40af" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>
            <div><div style={s.sTitle}>Identité</div><div style={s.sDesc}>Nom, pasteur, description</div></div>
          </div>
          <div style={s.sBody}>
            <div style={{ marginBottom: 12 }}>
              <label style={s.label}>Nom de l'église</label>
              <input style={s.input} value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={s.label}>Pasteur / Responsable</label>
              <input style={s.input} value={form.pastor} onChange={e => setForm(p => ({ ...p, pastor: e.target.value }))} placeholder="Ex: Pasteur Jean Dupont" />
            </div>
            <div>
              <label style={s.label}>Description / Slogan</label>
              <textarea style={{ ...s.input, height: 80, resize: 'vertical' }} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Présentez brièvement votre église..." />
            </div>
            {logoPreview && (
              <button onClick={removeLogo} style={{ marginTop: 12, background: 'none', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 6, padding: '5px 12px', fontSize: '0.78rem', cursor: 'pointer' }}>
                Supprimer le logo
              </button>
            )}
          </div>
        </div>

        {/* Localisation */}
        <div style={s.section}>
          <div style={s.sHead}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
            <div><div style={s.sTitle}>Localisation</div><div style={s.sDesc}>Ville, pays, adresse</div></div>
          </div>
          <div style={s.sBody}>
            <div style={{ ...s.row2, marginBottom: 12 }}>
              <div>
                <label style={s.label}>Ville</label>
                <input style={s.input} value={form.city} onChange={e => setForm(p => ({ ...p, city: e.target.value }))} placeholder="Ex: Abidjan" />
              </div>
              <div>
                <label style={s.label}>Pays</label>
                <input style={s.input} value={form.country} onChange={e => setForm(p => ({ ...p, country: e.target.value }))} />
              </div>
            </div>
            <div>
              <label style={s.label}>Adresse complète</label>
              <input style={s.input} value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} placeholder="Rue, quartier, BP..." />
            </div>
          </div>
        </div>

        {/* Contact */}
        <div style={s.section}>
          <div style={s.sHead}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 14a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.56 3h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
            <div><div style={s.sTitle}>Contact & Web</div><div style={s.sDesc}>Téléphone, email, réseaux</div></div>
          </div>
          <div style={s.sBody}>
            <div style={{ ...s.row2, marginBottom: 12 }}>
              <div>
                <label style={s.label}>Téléphone</label>
                <input style={s.input} value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="+225 07 XX XX XX XX" />
              </div>
              <div>
                <label style={s.label}>Email</label>
                <input style={s.input} type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="contact@eglise.ci" />
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={s.label}>Site web</label>
              <input style={s.input} value={form.website} onChange={e => setForm(p => ({ ...p, website: e.target.value }))} placeholder="https://..." />
            </div>
            <div>
              <label style={s.label}>Facebook / Réseaux sociaux</label>
              <input style={s.input} value={form.facebook} onChange={e => setForm(p => ({ ...p, facebook: e.target.value }))} placeholder="facebook.com/eglise" />
            </div>
          </div>
        </div>

        {/* Apparence */}
        <div style={s.section}>
          <div style={s.sHead}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2"><circle cx="13.5" cy="6.5" r=".5" fill="#f59e0b"/><circle cx="17.5" cy="10.5" r=".5" fill="#f59e0b"/><circle cx="8.5" cy="7.5" r=".5" fill="#f59e0b"/><circle cx="6.5" cy="12.5" r=".5" fill="#f59e0b"/><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"/></svg>
            <div><div style={s.sTitle}>Couleur principale</div><div style={s.sDesc}>Navbar, boutons, accents</div></div>
          </div>
          <div style={s.sBody}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
              {PRESET_COLORS.map(c => (
                <button key={c} onClick={() => setForm(p => ({ ...p, primary_color: c }))}
                  style={{ width: 32, height: 32, borderRadius: 8, background: c, border: form.primary_color === c ? '3px solid #fff' : '2px solid transparent', cursor: 'pointer', boxShadow: form.primary_color === c ? `0 0 0 2px ${c}` : 'none', transition: 'all 0.15s' }} />
              ))}
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <input type="color" value={form.primary_color} onChange={e => setForm(p => ({ ...p, primary_color: e.target.value }))}
                style={{ width: 42, height: 38, borderRadius: 8, border: '1.5px solid var(--border)', cursor: 'pointer', padding: 2, flexShrink: 0 }} />
              <input style={{ ...s.input, flex: 1 }} value={form.primary_color} onChange={e => setForm(p => ({ ...p, primary_color: e.target.value }))} placeholder="#1e40af" />
            </div>
            <div style={{ marginTop: 12, borderRadius: 8, background: color, color: '#fff', padding: '10px 16px', fontSize: '0.85rem', fontWeight: 600 }}>
              Aperçu — {form.name || church.name}
            </div>
          </div>
        </div>
      </div>

      {/* Bouton sauvegarde */}
      <div style={{ marginTop: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
        <button style={s.btn(color)} onClick={handleSave} disabled={saving}>
          {saving
            ? <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>Enregistrement...</>
            : <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>Sauvegarder les modifications</>
          }
        </button>
        {saved && <span style={{ color: '#22c55e', fontWeight: 600, fontSize: '0.875rem' }}>✓ Modifications enregistrées</span>}
      </div>
    </div>
  );
}
